import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { get, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import type { ILocation } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { fetchLayoutType } from '../layoutTypes';
import { mapLocation } from '../schedules/helper';
import { fetchViewLocationsByGroupsWithoutPagination } from './useViewLocationsByGroups';

export const useGetLocation = (
  locationCode = '',
  userRole?: AuthClassification | null,
  overrideQuerySetting?: UseQueryOptions<ILocation>,
) =>
  useQuery({
    queryKey: queryKeys.locations.details(locationCode),
    queryFn: () => fetchLocation(locationCode, userRole),
    enabled: !isEmpty(locationCode),
    ...overrideQuerySetting,
  });

const fetchLocation = async (
  locationCode = '',
  userRole?: AuthClassification | null,
) => {
  let url;
  if (userRole !== AuthClassification.OWNER_OPERATOR) {
    url = `/locations/${locationCode}${generateQuery([
      'locationGroups',
      'locationTags',
      'locationOwnerTags',
    ])}`;
  } else {
    url = `/locations/${locationCode}${generateQuery([
      'locationGroups',
      'locationOwnerTags',
    ])}`;
  }

  const [locationResponse, viewLocations, layoutTypes] = await Promise.all([
    requestCCApi({ url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.viewLocations.all(
        `locations.location_code=${locationCode}`,
      ),
      queryFn: () =>
        fetchViewLocationsByGroupsWithoutPagination([
          `locations.location_code=${locationCode}`,
        ]),
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
    }),
  ]);

  return mapLocation(
    locationResponse.data,
    get(locationResponse, 'includes.locationGroups.data', []),
    viewLocations,
    layoutTypes,
    get(locationResponse, 'includes.locationTags.data', []),
    get(locationResponse, 'includes.locationOwnerTags.data', []),
  );
};
